import { create } from 'zustand';

// Theme preference (T-020): persisted in localStorage, defaults to the OS
// preference. Applying the `dark` class on <html> drives Tailwind `dark:`
// variants and the CSS-variable palettes in index.css / App.css / editor.css.
const getInitialTheme = () => {
  try {
    const saved = localStorage.getItem('theme');
    if (saved === 'light' || saved === 'dark') return saved;
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
  } catch (e) {
    // localStorage/matchMedia unavailable (SSR, tests) — fall through.
  }
  return 'light';
};

const applyTheme = (theme) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
};

// T-217: nivel de contraste de accesibilidad. Sigue el MISMO patrón que theme
// (getInitialTheme/applyTheme/setTheme): localStorage init + función de
// aplicación al <html> + setter que persiste, aplica y set(). El atributo
// `data-contrast` en <html> dispara los overrides quirúrgicos de contrast.css.
//   'normal'  → sin atributo (comportamiento por defecto del tema base).
//   'alto'    → sube contraste de texto secundario y bordes (WCAG AA, ≥4.5:1).
//   'extremo' → base clara, negros puros y sin translucidez (WCAG AAA, ≥7:1).
const getInitialContrast = () => {
  try {
    const saved = localStorage.getItem('contrast');
    if (saved === 'normal' || saved === 'alto' || saved === 'extremo') return saved;
  } catch (e) {
    // localStorage unavailable (SSR, tests) — fall through.
  }
  return 'normal';
};

const applyContrast = (contrast) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (contrast === 'normal') {
    root.removeAttribute('data-contrast');
  } else {
    root.setAttribute('data-contrast', contrast);
  }
};

// T-222: paletas de colores alternativas (sepia, oceano, bosque, caramelo,
// violeta). Sigue el MISMO patrón que theme/contrast: localStorage init +
// función de aplicación al <html> (atributo `data-palette`) + setter que
// persiste, aplica y set(). '' = sin paleta (tema base). Los overrides por
// selector de atributo viven en palette.css (importado desde index.css).
// NO toca theme: la base claro/oscuro la fija la opción del dropdown.
const PALETTE_VALIDAS = ['sepia', 'oceano', 'bosque', 'caramelo', 'violeta'];

const getInitialPalette = () => {
  try {
    const saved = localStorage.getItem('palette');
    if (PALETTE_VALIDAS.includes(saved)) return saved;
  } catch (e) {
    // localStorage unavailable (SSR, tests) — fall through.
  }
  return '';
};

const applyPalette = (palette) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (!palette) {
    root.removeAttribute('data-palette');
  } else {
    root.setAttribute('data-palette', palette);
  }
};

// T-081: el historial conversacional dejó de ser global — ahora se elige POR
// conversación en el panel del chat (ChatSidebar), default 0 (stateless).

export const useConfigStore = create((set, get) => ({
  apiKey: localStorage.getItem('openrouter_api_key') || '',
  model: localStorage.getItem('openrouter_model') || 'openai/gpt-4o-mini',
  // T-031: image-generation model preference (separate from the chat model).
  imageModel: localStorage.getItem('openrouter_image_model') || 'google/gemini-2.5-flash-image-preview',
  // T-036: image-description (vision) model preference (separate from chat/image).
  describeModel: localStorage.getItem('openrouter_describe_model') || 'google/gemini-2.5-flash',
  theme: getInitialTheme(),
  // T-217: nivel de contraste (normal/alto/extremo). Mismo init localStorage
  // que theme; el pre-paint del atributo data-contrast vive en index.js.
  contrast: getInitialContrast(),
  // T-222: paleta de colores (sepia/oceano/bosque/caramelo/violeta; '' =
  // sin paleta). Mismo init localStorage que contrast; el pre-paint del
  // atributo data-palette vive en index.js. NO toca theme: la base claro/
  // oscuro la fija la opción del dropdown (setTheme).
  palette: getInitialPalette(),

  // T-081: historial conversacional persistido por sesión (default 10) y
  // contexto elegido (default 'none'). Siguen el mismo patrón localStorage
  // que apiKey/model.
  chatHistoryLimit: (() => {
    const v = localStorage.getItem('chat_history_limit');
    return v != null ? Number(v) : 10;
  })(),
  chatContext: localStorage.getItem('chat_context') || 'none',
  // T-147 (venía del configStore duplicado de tiptap): toggle "Chat global vs
  // Chat por hoja". Default FALSE = por hoja (cada hoja mantiene su
  // conversación; al cambiar de hoja se recarga). TRUE = una sola conversación
  // para TODO el documento: al scopear la sesión con sheetId=null el useEffect
  // de useChatSessions NO se re-dispara al cambiar de hoja. localStorage guarda
  // strings -> `=== 'true'` y se persiste 'true'/'false'.
  chatGlobal: localStorage.getItem('chat_global') === 'true',

  setChatGlobal: (chatGlobal) => {
    localStorage.setItem('chat_global', chatGlobal ? 'true' : 'false');
    set({ chatGlobal });
  },

  setApiKey: (key) => {
    localStorage.setItem('openrouter_api_key', key);
    set({ apiKey: key });
  },

  setModel: (model) => {
    localStorage.setItem('openrouter_model', model);
    set({ model });
  },

  setImageModel: (imageModel) => {
    localStorage.setItem('openrouter_image_model', imageModel);
    set({ imageModel });
  },

  setDescribeModel: (describeModel) => {
    localStorage.setItem('openrouter_describe_model', describeModel);
    set({ describeModel });
  },

  setChatHistoryLimit: (chatHistoryLimit) => {
    localStorage.setItem('chat_history_limit', String(chatHistoryLimit));
    set({ chatHistoryLimit });
  },

  setChatContext: (chatContext) => {
    localStorage.setItem('chat_context', chatContext);
    set({ chatContext });
  },

  // Llamado desde authStore.logout(). Borrar localStorage NO basta: este store
  // ya leyó los valores a memoria al cargar el módulo, y el logout navega a
  // /auth sin recargar la página (BrowserRouter). Sin este reset, el siguiente
  // usuario que iniciara sesión en la MISMA pestaña heredaba la llave BYOK del
  // anterior desde memoria. El tema, el contraste y la paleta NO se limpian:
  // son preferencias del equipo/dispositivo (accesibilidad/estética), no del
  // usuario.
  clearUserPrefs: () => {
    set({ apiKey: '', chatContext: 'none', chatHistoryLimit: 10, chatGlobal: false });
  },

  setTheme: (theme) => {
    try {
      localStorage.setItem('theme', theme);
    } catch (e) {
      // ignore persistence failure
    }
    applyTheme(theme);
    set({ theme });
  },

  toggleTheme: () => {
    const next = get().theme === 'dark' ? 'light' : 'dark';
    get().setTheme(next);
  },

  // T-217: setter de contraste (normal/alto/extremo). Persiste, aplica el
  // atributo data-contrast en <html> y actualiza el estado. Regla: el nivel
  // 'extremo' es siempre base CLARA (negros puros, sin translucidez), así que
  // fuerza setTheme('light') — no tiene sentido oscuro con extremo.
  setContrast: (contrast) => {
    try {
      localStorage.setItem('contrast', contrast);
    } catch (e) {
      // ignore persistence failure
    }
    applyContrast(contrast);
    if (contrast === 'extremo') {
      get().setTheme('light');
    }
    set({ contrast });
  },

  // T-222: setter de paleta. Persiste, aplica el atributo data-palette en
  // <html> y actualiza el estado. NO toca theme: la base claro/oscuro la fija
  // la opción del dropdown (setTheme), independientemente de la paleta.
  setPalette: (palette) => {
    try {
      localStorage.setItem('palette', palette);
    } catch (e) {
      // ignore persistence failure
    }
    applyPalette(palette);
    set({ palette });
  }
}));
